// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Ben Richardson <hi@ben.gy>
import L from 'leaflet';
import type { Ctx } from '../main.ts';
import type { Seat } from '../data.ts';
import { GROUP_META, GROUP_ORDER } from '../parties.ts';
import { fmtPct, fmtSigned, esc, fullName } from '../utils/format.ts';
import { term } from '../glossary.ts';

type Mode = 'winner' | 'tpp' | 'swing';

// Diverging Labor(red) ↔ Coalition(blue) mix. t in [0,1], 0=full Coalition.
function diverge(t: number): string {
  t = Math.max(0, Math.min(1, t));
  const coa = [28, 79, 161];
  const mid = [244, 246, 249];
  const alp = [224, 64, 63];
  const lerp = (a: number[], b: number[], u: number) => a.map((v, i) => Math.round(v + (b[i] - v) * u));
  const c = t < 0.5 ? lerp(coa, mid, t / 0.5) : lerp(mid, alp, (t - 0.5) / 0.5);
  return `rgb(${c[0]},${c[1]},${c[2]})`;
}

function fillFor(s: Seat, mode: Mode): string {
  if (mode === 'winner') return GROUP_META[s.winnerGroup].color;
  if (mode === 'tpp') return diverge(s.tppALPPct / 100);
  // swing: map ±8 points to full scale, centred at 0. No comparable prior → grey.
  if (s.tppSwingToALP == null) return '#cbd5e1';
  return diverge(0.5 + Math.max(-8, Math.min(8, s.tppSwingToALP)) / 16);
}

function tipHtml(s: Seat, mode: Mode): string {
  const win = s.tcp[0];
  const lead = `${fullName(win.given, win.surname)} · ${GROUP_META[win.group].label}`;
  let extra = '';
  if (mode === 'tpp') extra = `<br>TPP: Labor ${fmtPct(s.tppALPPct)} · Coalition ${fmtPct(s.tppLNPPct)}`;
  else if (mode === 'swing') extra = s.tppSwingToALP == null ? '<br>No comparable 2022 figure' : `<br>Swing ${fmtSigned(s.tppSwingToALP)} to Labor`;
  else extra = `<br>Margin ${s.marginPct.toFixed(s.marginPct < 0.1 ? 2 : 1)}%`;
  return `<b>${esc(s.name)}</b> <span style="opacity:.7">${esc(s.state)}</span><br>${esc(lead)}${extra}`;
}

export async function renderMap(root: HTMLElement, ctx: Ctx): Promise<void> {
  const seatByName = ctx.seatByName;
  let mode: Mode = 'winner';

  root.innerHTML = `
    <div class="view-head">
      <h2>The seat map</h2>
      <p class="subtitle">All 150 electorates. Shade by who won, by the ${term('tpp', 'two-party-preferred')} vote, or by the ${term('swing')}. Click any seat for its full result.</p>
    </div>
    <div class="panel">
      <div class="controls">
        <div class="seg" role="tablist" aria-label="Map colour mode">
          <button data-mode="winner" class="active">Winning party</button>
          <button data-mode="tpp">Two-party vote</button>
          <button data-mode="swing">Swing to Labor</button>
        </div>
      </div>
      <div class="map-shell"><div id="map" class="map-canvas"></div></div>
      <div class="legend" id="map-legend" style="margin-top:var(--sp-md)"></div>
      <p class="note" id="map-note"></p>
    </div>`;

  const legend = root.querySelector('#map-legend')!;
  const note = root.querySelector('#map-note')!;
  const renderLegend = () => {
    if (mode === 'winner') {
      legend.innerHTML = GROUP_ORDER.map((g) => `<span class="li"><span class="sw" style="background:${GROUP_META[g].color}"></span>${GROUP_META[g].label}</span>`).join('');
      note.textContent = 'Each seat is coloured by the party of its elected member — including teal and other independents.';
    } else if (mode === 'tpp') {
      legend.innerHTML = `<span class="li"><span class="sw" style="background:${diverge(0.05)}"></span>Coalition strong</span><span class="li"><span class="sw" style="background:${diverge(0.5)}"></span>50 / 50</span><span class="li"><span class="sw" style="background:${diverge(0.95)}"></span>Labor strong</span>`;
      note.textContent = 'Notional Labor-vs-Coalition share in every seat, even where the two majors did not finish first and second.';
    } else {
      legend.innerHTML = `<span class="li"><span class="sw" style="background:${diverge(0.05)}"></span>Swing to Coalition</span><span class="li"><span class="sw" style="background:${diverge(0.5)}"></span>No change</span><span class="li"><span class="sw" style="background:${diverge(0.95)}"></span>Swing to Labor</span><span class="li"><span class="sw" style="background:#cbd5e1"></span>No 2022 comparison</span>`;
      note.textContent = 'Change in the two-party vote since 2022. Most seats moved to Labor, but a band of regional seats swung the other way. Grey seats were non-classic in 2022, so no comparable two-party swing exists.';
    }
  };
  renderLegend();

  const canvas = root.querySelector('#map') as HTMLElement;
  const map = L.map(canvas, { minZoom: 3, maxZoom: 11, zoomControl: true, scrollWheelZoom: false, preferCanvas: false });
  map.attributionControl.setPrefix(false);
  L.tileLayer('https://{s}.basemaps.cartocdn.com/light_nolabels/{z}/{x}/{y}{r}.png', {
    attribution: 'Tiles © CARTO · Boundaries © AEC / Digital Atlas of Australia',
    subdomains: 'abcd', minZoom: 3, maxZoom: 11,
  }).addTo(map);

  let geo: GeoJSON.FeatureCollection;
  try {
    geo = await fetch('data/boundaries.geojson').then((r) => { if (!r.ok) throw new Error('boundaries'); return r.json(); });
  } catch {
    canvas.innerHTML = '<div class="state-msg">Could not load the map boundaries.</div>';
    return;
  }

  const layer = L.geoJSON(geo, {
    style: (f) => {
      const s = seatByName.get((f as any).properties.div);
      return { fillColor: s ? fillFor(s, mode) : '#ccc', fillOpacity: 0.82, color: '#ffffff', weight: 0.6 };
    },
    onEachFeature: (f, lyr) => {
      const s = seatByName.get((f as any).properties.div);
      if (!s) return;
      lyr.bindTooltip(tipHtml(s, mode), { sticky: true, className: 'map-tip' });
      lyr.on({
        mouseover: () => (lyr as L.Path).setStyle({ weight: 2, color: '#0f1c2e' }),
        mouseout: () => layer.resetStyle(lyr as L.Path),
        click: () => ctx.openSeat(s.name),
      });
    },
  }).addTo(map);

  const restyle = () => {
    layer.setStyle((f) => {
      const s = seatByName.get((f as any).properties.div);
      return { fillColor: s ? fillFor(s, mode) : '#ccc', fillOpacity: 0.82, color: '#ffffff', weight: 0.6 } as L.PathOptions;
    });
    layer.eachLayer((lyr) => {
      const s = seatByName.get(((lyr as any).feature).properties.div);
      if (s) (lyr as L.Layer).bindTooltip(tipHtml(s, mode), { sticky: true, className: 'map-tip' });
    });
  };

  root.querySelectorAll<HTMLButtonElement>('.seg [data-mode]').forEach((b) => {
    b.addEventListener('click', () => {
      mode = b.dataset.mode as Mode;
      root.querySelectorAll('.seg [data-mode]').forEach((x) => x.classList.toggle('active', x === b));
      renderLegend();
      restyle();
    });
  });

  const bounds = layer.getBounds();
  const fit = () => { map.invalidateSize(); if (bounds.isValid() && canvas.clientHeight > 50) map.fitBounds(bounds, { padding: [10, 10] }); };
  const ro = new ResizeObserver(() => { if (canvas.clientHeight > 50) { fit(); ro.disconnect(); } });
  ro.observe(canvas);
  setTimeout(fit, 400);
}
